import React, { useState, useEffect, useCallback } from 'react';
import { confirmDialog } from '../ConfirmDialog';
import {
  ListOrdered, RefreshCw, Loader2, CheckCircle, AlertCircle,
  Clock, Play, Pause, Trash2, ChevronDown, ChevronUp,
  Timer, Building2, Calendar, Zap, X, Copy, Database, ExternalLink, History,
  Download, HardDrive, Wrench, Scissors, BarChart3
} from 'lucide-react';
import apiClient from '../../lib/apiClient';
import useDepartments from '../../hooks/useDepartments';
import PoLink from '../PoDetailModal/PoLink';
import ScrapeHistoryCard from './ScrapeHistoryCard';
import './JobQueue.css';

const STATUS_CONFIG = {
  pending:     { label: 'Pending',     color: '#64748b', icon: Clock,       bg: 'rgba(100,116,139,0.12)' },
  claimed:     { label: 'Claimed',     color: '#3b82f6', icon: Zap,         bg: 'rgba(59,130,246,0.12)' },
  running:     { label: 'Running',     color: '#3b82f6', icon: Loader2,     bg: 'rgba(59,130,246,0.12)' },
  downloading: { label: 'Downloading', color: '#3b82f6', icon: Download,    bg: 'rgba(59,130,246,0.12)' },
  downloaded:  { label: 'Downloaded',  color: '#8b5cf6', icon: HardDrive,   bg: 'rgba(139,92,246,0.12)' },
  persisting:  { label: 'Saving',      color: '#8b5cf6', icon: Database,    bg: 'rgba(139,92,246,0.12)' },
  completed:   { label: 'Completed',   color: '#22c55e', icon: CheckCircle, bg: 'rgba(34,197,94,0.12)' },
  partial:     { label: 'Partial',     color: '#f59e0b', icon: AlertCircle, bg: 'rgba(245,158,11,0.12)' },
  failed:      { label: 'Failed',      color: '#ef4444', icon: AlertCircle, bg: 'rgba(239,68,68,0.12)' },
  cancelled:   { label: 'Cancelled',   color: '#64748b', icon: X,           bg: 'rgba(100,116,139,0.08)' },
};

// Phase display config with weights for overall progress calculation
// Weights represent typical time distribution: download 20%, persist+classify 65%, upload 15%
const PHASE_CONFIG = {
  download: { label: 'Downloading',  icon: '⬇️', color: '#3b82f6', start: 0,  end: 20 },
  waiting:  { label: 'Queued',       icon: '⏳', color: '#94a3b8', start: 20, end: 20 },
  persist:  { label: 'Saving',       icon: '💾', color: '#8b5cf6', start: 20, end: 55 },
  classify: { label: 'Classifying',  icon: '🏷️', color: '#f59e0b', start: 55, end: 90 },
  upload:   { label: 'Uploading',    icon: '☁️', color: '#06b6d4', start: 90, end: 100 },
  complete: { label: 'Complete',     icon: '✅', color: '#22c55e', start: 100, end: 100 },
};

// Calculate overall job progress from phase + phase percent + job status + stats.
// Job status is authoritative — if status says "downloaded" but phase says "download",
// download is actually complete (worker hasn't yet updated phase to "persist").
// We also derive a finer-grained percent from stats.linesUpserted / stats.totalLines
// when the worker reports those, because workers update `percent` coarsely
// (0 → 90 in one jump at end of phase), but write `linesUpserted` continuously.
function calcOverallProgress(progress, jobStatus) {
  const phase = progress?.phase;
  let phasePercent = progress?.percent || 0;
  const stats = progress?.stats || {};

  // For persist phase: prefer stats-based ratio (smoother + more honest than worker's coarse percent).
  // Worker writes linesUpserted continuously, so this fills the bar as work happens.
  if (phase === 'persist' && stats.totalLines > 0 && stats.linesUpserted != null) {
    const statsRatio = Math.min(100, (stats.linesUpserted / stats.totalLines) * 100);
    phasePercent = Math.max(phasePercent, statsRatio);
  }

  // Status-based floor: catches the gap where job has moved past download but
  // worker hasn't yet emitted a 'persist' phase update.
  const STATUS_MIN_PROGRESS = {
    downloading: 0,
    downloaded: 20,
    persisting: 22,    // slight bump above 20 so user sees movement on phase transition
    classifying: 55,
  };
  const minProgress = STATUS_MIN_PROGRESS[jobStatus] || 0;

  // Calculate progress from phase info
  const config = PHASE_CONFIG[phase];
  let phaseProgress = 0;
  if (config) {
    const phaseRange = config.end - config.start;
    phaseProgress = Math.round(config.start + (phaseRange * phasePercent / 100));
  } else {
    phaseProgress = phasePercent;
  }

  // Return the higher of the two (status-based or phase-based)
  return Math.max(minProgress, phaseProgress);
}

// Normalize result object to handle all scraper result formats:
// 1. Old list scraper: suppliers, newCount, duplicateCount, saved
// 2. Old worker.js bulk export: bulkExport.uniquePOs, persistence.headersUpserted
// 3. New persist worker: uniquePOs, headersUpserted, headersSkipped, posClassified
function normalizeJobResult(rawResult) {
  if (!rawResult) return {};

  const r = rawResult;
  // Extract nested fields from old worker.js bulk export format
  const bulkExport = r.bulkExport || {};
  const persistence = r.persistence || {};

  // Calculate total POs from any format (check nested first, then top-level)
  const totalPOs = r.uniquePOs
    ?? bulkExport.uniquePOs
    ?? r.suppliers
    ?? r.headersUpserted
    ?? persistence.headersUpserted
    ?? 0;

  // Line items from any format
  const totalLineItems = r.totalLineItems ?? bulkExport.totalLineItems ?? 0;

  // Headers/lines upserted (new or updated records)
  const headersUpserted = r.headersUpserted ?? persistence.headersUpserted ?? 0;
  const headersSkipped = r.headersSkipped ?? persistence.headersSkipped ?? 0;
  const linesUpserted = r.linesUpserted ?? persistence.linesUpserted ?? 0;
  const posClassified = r.posClassified ?? persistence.posClassified ?? 0;

  // For display: newCount/duplicateCount from old scraper, or derive from bulk export
  const newCount = r.newCount ?? headersUpserted ?? totalPOs;
  const duplicateCount = r.duplicateCount ?? headersSkipped ?? 0;
  const saved = r.saved ?? headersUpserted ?? newCount;

  // Duplicate rate calculation
  const totalProcessed = newCount + duplicateCount;
  const duplicateRate = r.duplicateRate ?? (totalProcessed > 0 ? Math.round((duplicateCount / totalProcessed) * 100) : 0);

  // Classification summary - check existing or build from bulk export fields
  let classificationSummary = r.classificationSummary;
  if (!classificationSummary && posClassified > 0) {
    const totalHeaders = headersUpserted || totalPOs;
    if (totalHeaders > 0) {
      classificationSummary = {
        classifiedPct: Math.round((posClassified / totalHeaders) * 100),
        total: totalHeaders,
        classified: posClassified,
      };
    }
  }

  // Duration - use totalDuration for bulk export or duration for old format
  const duration = r.totalDuration ?? r.duration;

  return {
    ...r,
    // Normalized fields
    totalPOs,
    totalLineItems,
    headersUpserted,
    headersSkipped,
    linesUpserted,
    posClassified,
    newCount,
    duplicateCount,
    saved,
    duplicateRate,
    classificationSummary,
    duration,
    // Keep original for reference
    _normalized: true,
  };
}

// Get display phase based on job status (status is more reliable than progress.phase)
function getDisplayPhase(jobStatus, progressPhase) {
  // If job status indicates a specific phase, use that
  if (jobStatus === 'downloading') return 'download';
  if (jobStatus === 'downloaded') {
    // Downloaded but not persisting yet = waiting for persist queue
    return progressPhase === 'persist' || progressPhase === 'classify' || progressPhase === 'upload'
      ? progressPhase
      : 'waiting';
  }
  if (jobStatus === 'persisting') {
    // Use the actual phase if available, otherwise default to persist
    return progressPhase || 'persist';
  }
  return progressPhase || 'download';
}

function timeAgo(dateStr) {
  if (!dateStr) return '';
  const seconds = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000);
  if (seconds < 60) return 'just now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  return `${Math.floor(seconds / 86400)}d ago`;
}

function formatDuration(seconds) {
  if (!seconds) return '-';
  if (seconds < 60) return `${seconds.toFixed(1)}s`;
  return `${Math.floor(seconds / 60)}m ${Math.round(seconds % 60)}s`;
}

function JobQueue({
  autoRefreshInterval = 10000,
  hideHeader = false,
  departmentCode = null,
  scrapeHistory = [],
  historyLoading = false,
  onRefreshHistory,
  onSupplierClick
}) {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const { deptMap } = useDepartments(); // Map of dept code -> acronym
  const [filter, setFilter] = useState('active'); // Default to showing active jobs
  const [expanded, setExpanded] = useState(true);
  const [selectedJob, setSelectedJob] = useState(null);
  const [jobSuppliers, setJobSuppliers] = useState([]);
  const [suppliersLoading, setSuppliersLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  const [cancelling, setCancelling] = useState(false);
  const [search, setSearch] = useState('');
  const [queueStats, setQueueStats] = useState(null); // Real-time queue stats from API
  const [selectedFailedJob, setSelectedFailedJob] = useState(null); // Failed job detail modal
  const [retrying, setRetrying] = useState(false);
  const [fixingProgress, setFixingProgress] = useState(false);

  const fetchJobs = useCallback(async () => {
    try {
      // Fetch jobs and real-time stats in parallel
      const [allJobs, pendingJobs, runningJobs, stats] = await Promise.all([
        apiClient.get('/api/scraper/jobs?limit=200'),
        apiClient.get('/api/scraper/jobs?status=pending&limit=200'),
        apiClient.get('/api/scraper/jobs?status=running&limit=10'),
        apiClient.get('/api/scraper/jobs/stats').catch(() => null), // Don't fail if stats unavailable
      ]);

      // Store real queue stats for accurate progress display
      if (stats) setQueueStats(stats);

      // Merge and dedupe: pending/running jobs might be in allJobs already
      const seen = new Set();
      const merged = [];
      for (const job of [
        ...(runningJobs.jobs || []),
        ...(pendingJobs.jobs || []),
        ...(allJobs.jobs || []),
      ]) {
        if (!seen.has(job.id)) {
          seen.add(job.id);
          merged.push(job);
        }
      }
      setJobs(merged);
    } catch (err) {
      console.error('Failed to fetch jobs:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const cancelJob = async (jobId, e) => {
    e?.stopPropagation();
    try {
      await apiClient.del(`/api/scraper/jobs/${jobId}`);
      setJobs(prev => prev.map(j => j.id === jobId ? { ...j, status: 'cancelled' } : j));
    } catch (err) {
      console.error('Cancel failed:', err);
    }
  };

  const cancelAllPending = async () => {
    if (!(await confirmDialog(`Cancel all ${pendingCount} pending jobs?`))) return;
    setCancelling(true);
    try {
      const result = await apiClient.post('/api/scraper/jobs/cancel-pending', {});
      fetchJobs();
    } catch (err) {
      console.error('Bulk cancel failed:', err);
    } finally {
      setCancelling(false);
    }
  };

  useEffect(() => {
    fetchJobs();
    const interval = setInterval(fetchJobs, autoRefreshInterval);
    return () => clearInterval(interval);
  }, [fetchJobs, autoRefreshInterval]);

  // First filter by department (if selected) and search
  const deptFilteredJobs = jobs.filter(j => {
    const p = typeof j.params === 'string' ? JSON.parse(j.params) : (j.params || {});
    // Department filter from TopNav selector
    if (departmentCode && p.departmentCode !== departmentCode) return false;
    // Search filter
    if (search) {
      const searchLower = search.toLowerCase();
      return (p.departmentCode || '').includes(search) ||
             (p.dateFrom || '').includes(search) ||
             (p.dateTo || '').includes(search) ||
             (p.acquisitionType || '').toLowerCase().includes(searchLower) ||
             (j.status || '').includes(searchLower);
    }
    return true;
  });

  // Then filter by status tab and sort appropriately
  // Active statuses include all in-progress phases: claimed, running, downloading, downloaded, persisting
  const filteredJobs = deptFilteredJobs.filter(j => {
    if (filter === 'queued' && j.status !== 'pending') return false;
    if (filter === 'active' && !['claimed', 'running', 'downloading', 'downloaded', 'persisting'].includes(j.status)) return false;
    if (filter === 'completed' && !['completed', 'partial', 'failed', 'cancelled'].includes(j.status)) return false;
    return true;
  }).sort((a, b) => {
    // For completed tab, sort by completion time (most recent first)
    if (filter === 'completed') {
      const aTime = a.completed_at ? new Date(a.completed_at).getTime() : 0;
      const bTime = b.completed_at ? new Date(b.completed_at).getTime() : 0;
      return bTime - aTime;
    }
    // For queued, sort by creation time (oldest first = FIFO)
    if (filter === 'queued') {
      const aTime = a.created_at ? new Date(a.created_at).getTime() : 0;
      const bTime = b.created_at ? new Date(b.created_at).getTime() : 0;
      return aTime - bTime;
    }
    // For active, sort by start time (most recently started first)
    if (filter === 'active') {
      const aTime = a.started_at ? new Date(a.started_at).getTime() : 0;
      const bTime = b.started_at ? new Date(b.started_at).getTime() : 0;
      return bTime - aTime;
    }
    return 0;
  });

  const handleJobClick = async (job) => {
    const result = typeof job.result === 'string' ? JSON.parse(job.result) : (job.result || {});
    const params = typeof job.params === 'string' ? JSON.parse(job.params) : (job.params || {});

    // Handle failed jobs with a different modal
    if (job.status === 'failed') {
      setSelectedFailedJob(job);
      return;
    }

    if (!['completed', 'partial'].includes(job.status)) return;
    setSelectedJob(job);
    setSuppliersLoading(true);

    try {
      let data = [];

      // First try: Use runId if available (most reliable)
      if (result.runId) {
        // Don't add date filtering - runId already scopes to the correct data
        // Date filtering was causing empty results when dates didn't match exactly
        data = await apiClient.get(`/api/scraper/results/${result.runId}/suppliers`);
      }

      // Second try: If runId is missing or returned empty, query by department + date range
      if ((!result.runId || (Array.isArray(data) && data.length === 0)) && params.departmentCode) {
        console.log('[JobQueue] Falling back to dept+date query for job:', job.id);
        const queryParams = new URLSearchParams();
        queryParams.set('departmentCode', params.departmentCode);
        if (params.dateFrom) queryParams.set('dateFrom', params.dateFrom);
        if (params.dateTo) queryParams.set('dateTo', params.dateTo);
        if (params.acquisitionType) queryParams.set('acquisitionType', params.acquisitionType);

        try {
          const fallbackData = await apiClient.get(`/api/scraper/suppliers?${queryParams.toString()}`);
          if (Array.isArray(fallbackData) && fallbackData.length > 0) {
            data = fallbackData;
          }
        } catch (fallbackErr) {
          // Fallback endpoint might not exist - that's okay, we tried
          console.log('[JobQueue] Fallback query not available:', fallbackErr.message);
        }
      }

      setJobSuppliers(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('[JobQueue] Failed to fetch suppliers:', err);
      setJobSuppliers([]);
    } finally {
      setSuppliersLoading(false);
    }
  };

  const handleCopyAll = () => {
    if (!selectedJob || jobSuppliers.length === 0) return;
    const params = typeof selectedJob.params === 'string' ? JSON.parse(selectedJob.params) : selectedJob.params;
    const result = typeof selectedJob.result === 'string' ? JSON.parse(selectedJob.result) : selectedJob.result;
    const lines = [
      `Scrape Job: ${selectedJob.id}`,
      `Department: ${params.departmentCode} | Type: ${params.acquisitionType}`,
      `Date Range: ${params.dateFrom} → ${params.dateTo}`,
      `POs Captured: ${result?.suppliers || 0}${result?.totalExpected ? ' of ' + result.totalExpected : ''}${result?.hasMorePages ? ' (more available)' : ''} | Duration: ${result?.duration}s`,
      '',
      'SUPPLIER'.padEnd(35) + 'AMOUNT'.padEnd(15) + 'DOCUMENT #'.padEnd(20) + 'DATE',
      '─'.repeat(85),
    ];
    jobSuppliers.forEach(s => {
      lines.push(
        (s.supplier_name || '?').substring(0, 34).padEnd(35) +
        ('$' + parseFloat(s.amount || 0).toLocaleString()).padEnd(15) +
        (s.document_number || '-').substring(0, 19).padEnd(20) +
        (s.date || '-')
      );
    });
    navigator.clipboard.writeText(lines.join('\n'));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleCancelJob = async (e, jobId) => {
    e.stopPropagation();
    if (!(await confirmDialog('Cancel this job?'))) return;
    try {
      await apiClient.post(`/api/scraper/jobs/${jobId}/cancel`);
      fetchJobs(); // Refresh the list
    } catch (err) {
      console.error('Failed to cancel job:', err);
      alert('Failed to cancel job: ' + (err.message || 'Unknown error'));
    }
  };

  const handleRetryFailedJob = async (jobId) => {
    setRetrying(true);
    try {
      await apiClient.post(`/api/scraper/jobs/${jobId}/retry`);
      setSelectedFailedJob(null);
      fetchJobs();
    } catch (err) {
      console.error('Failed to retry job:', err);
      alert('Failed to retry job: ' + (err.message || 'Unknown error'));
    } finally {
      setRetrying(false);
    }
  };

  const handleDeleteFailedJob = async (jobId) => {
    try {
      await apiClient.del(`/api/scraper/jobs/${jobId}`);
      setSelectedFailedJob(null);
      fetchJobs();
    } catch (err) {
      console.error('Failed to delete job:', err);
      alert('Failed to delete job: ' + (err.message || 'Unknown error'));
    }
  };

  const handleClearAllFailedJobs = async () => {
    const failedJobsList = deptFilteredJobs.filter(j => j.status === 'failed');
    if (failedJobsList.length === 0) return;
    if (!(await confirmDialog(`Delete all ${failedJobsList.length} failed jobs?`))) return;

    try {
      await Promise.all(failedJobsList.map(j => apiClient.del(`/api/scraper/jobs/${j.id}`)));
      fetchJobs();
    } catch (err) {
      console.error('Failed to clear failed jobs:', err);
      alert('Failed to clear some jobs: ' + (err.message || 'Unknown error'));
      fetchJobs(); // Refresh anyway to show what was deleted
    }
  };

  // Fix stale progress data for downloaded jobs
  const handleFixProgress = async () => {
    setFixingProgress(true);
    try {
      const result = await apiClient.post('/api/scraper/jobs/fix-progress');
      if (result.fixed > 0) {
        alert(`Fixed progress data for ${result.fixed} jobs`);
        fetchJobs(); // Refresh to show corrected data
      } else {
        alert('No stale progress data found');
      }
    } catch (err) {
      console.error('Failed to fix progress:', err);
      alert('Failed to fix progress: ' + (err.message || 'Unknown error'));
    } finally {
      setFixingProgress(false);
    }
  };

  // Cancel redundant jobs (already-scraped date ranges)
  const [cancellingRedundant, setCancellingRedundant] = useState(false);
  const handleCancelRedundant = async () => {
    setCancellingRedundant(true);
    try {
      // First do dry run to show what will be cancelled
      const dryRun = await apiClient.post('/api/scraper/jobs/cancel-redundant', { dryRun: true });

      if (dryRun.wouldCancel === 0) {
        alert('No redundant jobs found. All pending jobs are for new date ranges.');
        return;
      }

      const deptSummary = Object.entries(dryRun.byDepartment || {})
        .map(([dept, count]) => `${dept}: ${count}`)
        .join('\n');

      const confirmMsg = `Found ${dryRun.wouldCancel} redundant jobs (date ranges already scraped):\n\n${deptSummary}\n\nCancel these jobs?`;

      if (!(await confirmDialog(confirmMsg))) return;

      // Actually cancel
      const result = await apiClient.post('/api/scraper/jobs/cancel-redundant', { dryRun: false });
      alert(`Cancelled ${result.cancelled} redundant jobs`);
      fetchJobs();
    } catch (err) {
      console.error('Failed to cancel redundant jobs:', err);
      alert('Failed to cancel redundant jobs: ' + (err.message || 'Unknown error'));
    } finally {
      setCancellingRedundant(false);
    }
  };

  // Analyze failures
  const [analyzingFailures, setAnalyzingFailures] = useState(false);
  const [failureAnalysis, setFailureAnalysis] = useState(null);
  const handleAnalyzeFailures = async () => {
    setAnalyzingFailures(true);
    try {
      const result = await apiClient.get('/api/scraper/jobs/failures?hours=48');
      setFailureAnalysis(result);
    } catch (err) {
      console.error('Failed to analyze failures:', err);
      alert('Failed to analyze failures: ' + (err.message || 'Unknown error'));
    } finally {
      setAnalyzingFailures(false);
    }
  };

  // Counts from filtered job list (for tabs showing visible jobs)
  const visiblePendingCount = deptFilteredJobs.filter(j => j.status === 'pending').length;
  const visibleRunningCount = deptFilteredJobs.filter(j => ['running', 'claimed', 'downloading', 'downloaded', 'persisting'].includes(j.status)).length;
  const visibleCompletedCount = deptFilteredJobs.filter(j => ['completed', 'partial'].includes(j.status)).length;
  const visibleFailedCount = deptFilteredJobs.filter(j => j.status === 'failed').length;
  const historyCount = deptFilteredJobs.filter(j => ['completed', 'partial', 'failed', 'cancelled'].includes(j.status)).length;

  // TRUE counts from database (queueStats) - use these for banner/progress
  // Fall back to visible counts if stats not available
  const pendingCount = queueStats?.pending ?? visiblePendingCount;
  const runningCount = queueStats?.running ?? visibleRunningCount;
  const completedCount = (queueStats?.completed ?? 0) + (queueStats?.partial ?? 0) || visibleCompletedCount;
  const failedCount = queueStats?.failed ?? visibleFailedCount;
  const activeCount = pendingCount + runningCount;

  // Calculate throughput and ETA from recently completed jobs.
  // Filter out trivially-small completions (< 10 POs) — they bias avg downward
  // and produce wildly-optimistic ETAs when the queue actually has large jobs.
  const recentCompleted = deptFilteredJobs
    .filter(j => j.status === 'completed' || j.status === 'partial')
    .filter(j => j.completed_at) // Only jobs with completion timestamps
    .filter(j => {
      const r = typeof j.result === 'string' ? JSON.parse(j.result) : (j.result || {});
      return (r.uniquePOs ?? r.suppliers ?? 0) >= 10;
    })
    .slice(0, 20); // Use more jobs for better average

  // Average duration in seconds
  const avgDuration = recentCompleted.length > 0
    ? recentCompleted.reduce((sum, j) => {
        const r = typeof j.result === 'string' ? JSON.parse(j.result) : (j.result || {});
        return sum + (r.duration || 120);
      }, 0) / recentCompleted.length
    : null;

  // Calculate jobs/hour throughput from timestamps
  let throughputPerHour = null;
  if (recentCompleted.length >= 2) {
    const times = recentCompleted
      .map(j => new Date(j.completed_at).getTime())
      .filter(t => !isNaN(t))
      .sort((a, b) => b - a); // Most recent first
    if (times.length >= 2) {
      const timeSpanMs = times[0] - times[times.length - 1];
      const timeSpanHours = timeSpanMs / (1000 * 60 * 60);
      if (timeSpanHours > 0) {
        throughputPerHour = Math.round(times.length / timeSpanHours);
      }
    }
  }

  // ETA calculation: pending jobs / throughput
  const totalRemaining = pendingCount + runningCount;
  const etaMinutes = throughputPerHour && totalRemaining > 0
    ? Math.round((totalRemaining / throughputPerHour) * 60)
    : avgDuration && totalRemaining > 0
      ? Math.round((totalRemaining * avgDuration) / 60)
      : null;

  // Format ETA nicely
  const formatEta = (minutes) => {
    if (!minutes) return null;
    if (minutes < 60) return `~${minutes}m`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours >= 24) {
      const days = Math.floor(hours / 24);
      const remHours = hours % 24;
      return `~${days}d ${remHours}h`;
    }
    return mins > 0 ? `~${hours}h ${mins}m` : `~${hours}h`;
  };

  // Queue progress - only meaningful when we have pending work
  // When pending > 0, show progress based on how many we've completed recently vs remaining
  // "Recently" = jobs completed in last 24 hours (approximate current session)
  const recentlyCompletedCount = deptFilteredJobs.filter(j => {
    if (!['completed', 'partial'].includes(j.status)) return false;
    if (!j.completed_at) return false;
    const hoursAgo = (Date.now() - new Date(j.completed_at).getTime()) / (1000 * 60 * 60);
    return hoursAgo < 24; // Jobs completed in last 24 hours
  }).length;

  // Current workload = recently completed + what's still to do
  const currentWorkload = recentlyCompletedCount + pendingCount + runningCount;
  const queueCompletePct = currentWorkload > 0
    ? Math.round((recentlyCompletedCount / currentWorkload) * 100)
    : 0;

  // Get all active jobs for display (includes all in-progress phases)
  const runningJobs = deptFilteredJobs.filter(j =>
    ['running', 'claimed', 'downloading', 'downloaded', 'persisting'].includes(j.status)
  );

  return (
    <div className="jq-container">
      {!hideHeader && (
        <div className="jq-header" onClick={() => setExpanded(!expanded)}>
          <div className="jq-header-left">
            <ListOrdered size={16} />
            <h3>Job Queue</h3>
            {activeCount > 0 && (
              <span className="jq-active-badge">
                {runningCount > 0 && <Loader2 size={10} className="jq-spin" />}
                {activeCount} active
              </span>
            )}
          </div>
          <div className="jq-header-right">
            <button className="jq-btn jq-btn-ghost" onClick={(e) => { e.stopPropagation(); fetchJobs(); }}>
              <RefreshCw size={12} />
            </button>
            {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </div>
        </div>
      )}

      {(hideHeader || expanded) && (
        <div className="jq-body">
          {/* Filter Tabs + Search + Cancel */}
          <div className="jq-toolbar">
            <div className="jq-filters">
              <button className={`jq-filter ${filter === 'queued' ? 'active' : ''}`} onClick={() => setFilter('queued')}>
                Queued ({pendingCount})
              </button>
              <button className={`jq-filter ${filter === 'active' ? 'active' : ''}`} onClick={() => setFilter('active')}>
                Active ({runningCount})
              </button>
              <button className={`jq-filter ${filter === 'completed' ? 'active' : ''}`} onClick={() => setFilter('completed')}>
                Completed ({historyCount})
              </button>
              <button className={`jq-filter ${filter === 'runs' ? 'active' : ''}`} onClick={() => setFilter('runs')}>
                <History size={12} /> Runs ({scrapeHistory.length})
              </button>
            </div>
            <div className="jq-toolbar-right">
              <input
                className="jq-search"
                type="text"
                placeholder="Search dept, date, type..."
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
              {filter === 'completed' && visibleFailedCount > 0 && (
                <button
                  className="jq-btn jq-btn-clear-failed"
                  onClick={handleClearAllFailedJobs}
                  title={`Clear all ${visibleFailedCount} failed jobs`}
                >
                  <Trash2 size={12} />
                  Clear {visibleFailedCount} failed
                </button>
              )}
              {pendingCount > 0 && (
                <button
                  className="jq-btn jq-btn-cancel"
                  onClick={cancelAllPending}
                  disabled={cancelling}
                  title={`Cancel all ${pendingCount} pending jobs`}
                >
                  <Trash2 size={12} />
                  {cancelling ? 'Cancelling...' : `Cancel ${pendingCount} pending`}
                </button>
              )}
              <button
                className="jq-btn jq-btn-fix"
                onClick={handleFixProgress}
                disabled={fixingProgress}
                title="Fix stale progress data for downloaded jobs"
              >
                <Wrench size={12} />
                {fixingProgress ? 'Fixing...' : 'Fix Progress'}
              </button>
              {pendingCount > 10 && (
                <button
                  className="jq-btn jq-btn-redundant"
                  onClick={handleCancelRedundant}
                  disabled={cancellingRedundant}
                  title="Cancel pending jobs for date ranges already scraped"
                >
                  <Scissors size={12} />
                  {cancellingRedundant ? 'Checking...' : 'Cancel Redundant'}
                </button>
              )}
              <button
                className="jq-btn jq-btn-analyze"
                onClick={handleAnalyzeFailures}
                disabled={analyzingFailures}
                title="Analyze recent job failures"
              >
                <BarChart3 size={12} />
                {analyzingFailures ? 'Analyzing...' : 'Analyze Failures'}
              </button>
            </div>
          </div>

          {/* Failure Analysis Modal */}
          {failureAnalysis && (
            <div className="jq-failure-analysis">
              <div className="jq-failure-header">
                <h4>Failure Analysis (Last 48h)</h4>
                <button className="jq-close-btn" onClick={() => setFailureAnalysis(null)}>
                  <X size={16} />
                </button>
              </div>
              <div className="jq-failure-summary">
                <div className="jq-failure-total">
                  <strong>{failureAnalysis.total}</strong> failures
                </div>
                <div className="jq-failure-categories">
                  {failureAnalysis.byCategory.timeout > 0 && (
                    <span className="jq-failure-cat jq-cat-timeout">Timeout: {failureAnalysis.byCategory.timeout}</span>
                  )}
                  {failureAnalysis.byCategory.download_failed > 0 && (
                    <span className="jq-failure-cat jq-cat-download">Download Failed: {failureAnalysis.byCategory.download_failed}</span>
                  )}
                  {failureAnalysis.byCategory.throttle_403 > 0 && (
                    <span className="jq-failure-cat jq-cat-throttle">Throttle/403: {failureAnalysis.byCategory.throttle_403}</span>
                  )}
                  {failureAnalysis.byCategory.navigation > 0 && (
                    <span className="jq-failure-cat jq-cat-nav">Navigation: {failureAnalysis.byCategory.navigation}</span>
                  )}
                  {failureAnalysis.byCategory.other > 0 && (
                    <span className="jq-failure-cat jq-cat-other">Other: {failureAnalysis.byCategory.other}</span>
                  )}
                </div>
              </div>
              <div className="jq-failure-by-dept">
                <strong>By Department:</strong>
                {Object.entries(failureAnalysis.byDepartment || {}).map(([dept, count]) => (
                  <span key={dept} className="jq-dept-failure">{dept}: {count}</span>
                ))}
              </div>
              {failureAnalysis.sampleErrors && (
                <div className="jq-failure-samples">
                  <strong>Sample Errors:</strong>
                  {failureAnalysis.sampleErrors.timeout?.slice(0, 2).map((err, i) => (
                    <div key={`t${i}`} className="jq-sample-error jq-sample-timeout">{err?.substring(0, 80)}</div>
                  ))}
                  {failureAnalysis.sampleErrors.download_failed?.slice(0, 2).map((err, i) => (
                    <div key={`d${i}`} className="jq-sample-error jq-sample-download">{err?.substring(0, 80)}</div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Queue Status Banner — shows overall queue progress */}
          {activeCount > 0 && (
            <div className={`jq-worker-banner ${runningCount > 0 ? 'jq-worker-active' : 'jq-worker-idle'}`}>
              {runningCount > 0 ? (
                <>
                  {/* Top row: Running count, Pending count, ETA */}
                  <div className="jq-queue-header">
                    <div className="jq-queue-counts">
                      <span className="jq-queue-running">
                        <Loader2 size={12} className="jq-spin" />
                        <strong>{runningCount}</strong> running
                      </span>
                      <span className="jq-queue-separator">·</span>
                      <span className="jq-queue-pending">
                        <strong>{pendingCount}</strong> pending
                      </span>
                    </div>
                    {etaMinutes && (
                      <span className="jq-queue-eta">{formatEta(etaMinutes)} remaining</span>
                    )}
                  </div>

                  {/* Progress bar with percentage */}
                  <div className="jq-queue-progress">
                    <div className="jq-queue-progress-bar">
                      <div
                        className="jq-queue-progress-fill"
                        style={{ width: `${Math.max(2, queueCompletePct)}%` }}
                      />
                    </div>
                    <span className="jq-queue-progress-pct">{queueCompletePct}%</span>
                  </div>

                  {/* Bottom row: Throughput stats - only current session metrics */}
                  <div className="jq-queue-stats">
                    {throughputPerHour && (
                      <span className="jq-stat-throughput">
                        <Zap size={10} /> {throughputPerHour} jobs/hr
                      </span>
                    )}
                    {avgDuration && (
                      <span className="jq-stat-avg">
                        <Timer size={10} /> Avg: {formatDuration(avgDuration)}
                      </span>
                    )}
                    {recentlyCompletedCount > 0 && (
                      <span className="jq-stat-processed">
                        <CheckCircle size={10} /> {recentlyCompletedCount} done today
                      </span>
                    )}
                  </div>
                </>
              ) : (
                <div className="jq-worker-status">
                  <Clock size={16} />
                  <div className="jq-worker-info">
                    <span className="jq-worker-title">{pendingCount} job{pendingCount !== 1 ? 's' : ''} waiting for worker</span>
                    <span className="jq-worker-detail">Worker may be starting up or backend is redeploying</span>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Runs Tab Content */}
          {filter === 'runs' ? (
            <div className="jq-runs-content">
              {historyLoading && scrapeHistory.length === 0 ? (
                <div className="jq-loading"><Loader2 size={16} className="jq-spin" /> Loading runs...</div>
              ) : scrapeHistory.length === 0 ? (
                <div className="jq-empty">
                  <Database size={24} style={{ marginBottom: 8, opacity: 0.5 }} />
                  <div>{departmentCode ? 'No scrapes for this department' : 'No scrapes yet — run one above'}</div>
                </div>
              ) : (
                <div className="jq-history-cards">
                  {scrapeHistory.slice(0, 10).map((run, i) => (
                    <ScrapeHistoryCard
                      key={run.id || i}
                      run={run}
                      defaultExpanded={i === 0}
                      onRefresh={onRefreshHistory}
                      onSupplierClick={onSupplierClick}
                    />
                  ))}
                  {scrapeHistory.length > 10 && (
                    <div className="jq-history-more">
                      Showing 10 of {scrapeHistory.length} runs
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : (
          /* Job List */
          loading && jobs.length === 0 ? (
            <div className="jq-loading"><Loader2 size={16} className="jq-spin" /> Loading queue...</div>
          ) : filteredJobs.length === 0 ? (
            <div className="jq-empty">
              {filter === 'queued' && 'No jobs in queue'}
              {filter === 'active' && 'No jobs currently running'}
              {filter === 'completed' && 'No completed jobs'}
              {!['queued', 'active', 'completed'].includes(filter) && 'No jobs found'}
            </div>
          ) : (
            <div className="jq-list">
              {filteredJobs.map(job => {
                const config = STATUS_CONFIG[job.status] || STATUS_CONFIG.pending;
                const StatusIcon = config.icon;
                const params = typeof job.params === 'string' ? JSON.parse(job.params) : (job.params || {});
                const rawResult = typeof job.result === 'string' ? JSON.parse(job.result) : (job.result || {});
                const result = normalizeJobResult(rawResult);
                const isActive = ['pending', 'claimed', 'running', 'downloading', 'downloaded', 'persisting'].includes(job.status);

                // For sub-jobs (auto-subdivide), compute batch position
                const parentId = params.parentJobId;
                let batchInfo = null;
                if (parentId) {
                  const siblings = jobs.filter(j => {
                    const p = typeof j.params === 'string' ? JSON.parse(j.params) : (j.params || {});
                    return p.parentJobId === parentId;
                  }).sort((a, b) => {
                    const pa = typeof a.params === 'string' ? JSON.parse(a.params) : (a.params || {});
                    const pb = typeof b.params === 'string' ? JSON.parse(b.params) : (b.params || {});
                    return (pa.dateFrom || '').localeCompare(pb.dateFrom || '');
                  });
                  const batchIndex = siblings.findIndex(s => s.id === job.id);
                  batchInfo = { index: batchIndex + 1, total: siblings.length };
                }

                return (
                  <div
                    key={job.id}
                    className={`jq-job ${isActive ? 'jq-job-active' : ''} ${['running', 'claimed', 'downloading', 'downloaded', 'persisting'].includes(job.status) ? 'jq-job-running' : ''} ${['completed', 'partial', 'failed'].includes(job.status) ? 'jq-job-clickable' : ''} ${job.status === 'failed' ? 'jq-job-failed' : ''}`}
                    onClick={() => ['completed', 'partial', 'failed'].includes(job.status) && handleJobClick(job)}
                  >
                    <div className="jq-job-status" style={{ background: config.bg }}>
                      <StatusIcon
                        size={14}
                        style={{ color: config.color }}
                        className={['running', 'downloading', 'persisting'].includes(job.status) ? 'jq-spin' : ''}
                      />
                    </div>

                    <div className="jq-job-info">
                      <div className="jq-job-title">
                        <span className="jq-job-type">{(job.type || 'scprs').toUpperCase()}</span>
                        {/* Bulk export indicator - bulk is default for scprs jobs unless legacy mode */}
                        {(job.type !== 'scprs_detail' && !params.useLegacyScrape) && (
                          <span className="jq-job-bulk">Bulk</span>
                        )}
                        {params.departmentCode && (
                          <span className="jq-job-dept">
                            <Building2 size={10} /> {deptMap[params.departmentCode] || params.departmentCode}
                          </span>
                        )}
                        {params.acquisitionType && (
                          <span className="jq-job-acq">{params.acquisitionType}</span>
                        )}
                      </div>
                      <div className="jq-job-meta">
                        {params.dateFrom && (
                          <span><Calendar size={10} /> {params.dateFrom} → {params.dateTo}</span>
                        )}
                        {/* For running jobs, show when started; for others show when created */}
                        <span className="jq-job-time">
                          {['running', 'claimed', 'downloading', 'downloaded', 'persisting'].includes(job.status) && job.started_at
                            ? `started ${timeAgo(job.started_at)}`
                            : timeAgo(job.created_at)
                          }
                        </span>
                        {job.created_by && job.created_by !== 'unknown' && (
                          <span className="jq-job-source">by {job.created_by}</span>
                        )}
                      </div>
                    </div>

                    <div className="jq-job-result">
                      {(job.status === 'completed' || job.status === 'partial') && (
                        <>
                          <span className={`jq-result-suppliers${job.status === 'partial' ? ' jq-partial' : ''}${result.duplicateRate >= 95 ? ' jq-all-dup' : ''}`}>
                            {result.duplicateCount > 0 ? (
                              result.duplicateRate >= 95
                                ? `${result.saved || 0} POs (all dup)`
                                : `${result.newCount} new + ${result.duplicateCount} dup`
                            ) : `${result.totalPOs} POs`}
                          </span>
                          {batchInfo && (
                            <span className="jq-batch-info">batch {batchInfo.index} of {batchInfo.total}</span>
                          )}
                          {result.classificationSummary && (
                            <span
                              className="jq-classify-summary"
                              title={`Method breakdown: ${(result.classificationSummary.methodBreakdown || []).slice(0,8).map(m => `${m.method}=${m.count}`).join(', ')}`}
                            >
                              🏷️ {result.classificationSummary.classifiedPct}% classified
                              {result.classificationSummary.avgConfidence != null && (
                                <>  · avg {(result.classificationSummary.avgConfidence * 100).toFixed(0)}% conf</>
                              )}
                              {result.classificationSummary.needsReview > 0 && (
                                <>  · {result.classificationSummary.needsReview} need review</>
                              )}
                            </span>
                          )}
                          <span className="jq-result-duration">{formatDuration(result.duration)}</span>
                        </>
                      )}
                      {job.status === 'failed' && (
                        <span className="jq-result-error" title={job.error}>{(job.error || 'Unknown error').substring(0, 40)}</span>
                      )}
                      {['running', 'claimed', 'downloading', 'downloaded', 'persisting'].includes(job.status) && (
                        <>
                          {(job.progress?.phase || job.status === 'downloaded' || job.status === 'persisting') ? (() => {
                            // Use display phase based on job status (more reliable than progress.phase)
                            const displayPhase = getDisplayPhase(job.status, job.progress?.phase);
                            const overallPct = calcOverallProgress(job.progress, job.status);
                            const phaseConfig = PHASE_CONFIG[displayPhase] || PHASE_CONFIG.download;
                            // Prefer worker-reported message when present — much more informative
                            // than the static phase label ("Persisting 273 POs..." vs "Saving").
                            const phaseLabel = job.progress?.message || phaseConfig.label;
                            return (
                              <div className="jq-unified-progress">
                                {/* Overall progress bar */}
                                <div className="jq-unified-bar">
                                  <div
                                    className="jq-unified-fill"
                                    style={{ width: `${overallPct}%` }}
                                  />
                                  {/* Phase markers */}
                                  <div className="jq-phase-marker" style={{ left: '20%' }} title="Download complete" />
                                  <div className="jq-phase-marker" style={{ left: '55%' }} title="Persist complete" />
                                  <div className="jq-phase-marker" style={{ left: '90%' }} title="Classify complete" />
                                </div>
                                <span className="jq-unified-percent">{overallPct}%</span>
                                {/* Current phase indicator */}
                                <span className="jq-phase-tag" style={{ background: `${phaseConfig.color}22`, color: phaseConfig.color }} title={phaseConfig.label}>
                                  {phaseConfig.icon} {phaseLabel}
                                </span>
                                {/* Stats: lines progress */}
                                {job.progress?.stats?.linesUpserted != null && (
                                  <span className="jq-phase-stats">
                                    {job.progress.stats.totalLines ? (
                                      `${job.progress.stats.linesUpserted.toLocaleString()} / ${job.progress.stats.totalLines.toLocaleString()} lines`
                                    ) : (
                                      `${job.progress.stats.linesUpserted.toLocaleString()} lines`
                                    )}
                                  </span>
                                )}
                                {/* Classification stats (live, updated each batch) */}
                                {job.progress?.stats?.classificationStats && (
                                  <span
                                    className="jq-classify-stats"
                                    title={`Top methods: ${(job.progress.stats.classificationStats.topMethods || []).map(m => `${m.method}=${m.count}`).join(', ')}`}
                                  >
                                    🏷️ {job.progress.stats.classificationStats.classified.toLocaleString()}/{job.progress.stats.classificationStats.inserted.toLocaleString()} classified
                                    {job.progress.stats.classificationStats.avgConfidence != null && (
                                      <>  · avg {(job.progress.stats.classificationStats.avgConfidence * 100).toFixed(0)}% conf</>
                                    )}
                                    {job.progress.stats.classificationStats.needsReview > 0 && (
                                      <>  · {job.progress.stats.classificationStats.needsReview} need review</>
                                    )}
                                  </span>
                                )}
                              </div>
                            );
                          })() : (
                            <span className="jq-result-running"><Loader2 size={10} className="jq-spin" /> Starting...</span>
                          )}
                          {job.started_at && (
                            <span className="jq-result-duration">{formatDuration(Math.floor((Date.now() - new Date(job.started_at).getTime()) / 1000))}</span>
                          )}
                        </>
                      )}
                      {['pending', 'running', 'claimed', 'downloading', 'downloaded', 'persisting'].includes(job.status) && (
                        <button className="jq-cancel-btn" onClick={(e) => cancelJob(job.id, e)} title="Cancel this job">
                          <X size={12} /> {['running', 'downloading'].includes(job.status) ? 'Stop' : 'Cancel'}
                        </button>
                      )}
                      {job.status === 'cancelled' && (
                        <span className="jq-result-cancelled">Cancelled</span>
                      )}
                    </div>

                    <div className="jq-job-actions">
                      {isActive && (
                        <button
                          className="jq-cancel-btn"
                          onClick={(e) => handleCancelJob(e, job.id)}
                          title="Cancel job"
                        >
                          <X size={14} />
                        </button>
                      )}
                      <div className="jq-job-badge" style={{ background: config.bg, color: config.color }}>
                        {config.label}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )
          )}
        </div>
      )}

      {/* Job Drill-Down Modal */}
      {selectedJob && (() => {
        const params = typeof selectedJob.params === 'string' ? JSON.parse(selectedJob.params) : selectedJob.params;
        const rawResult = typeof selectedJob.result === 'string' ? JSON.parse(selectedJob.result) : selectedJob.result;
        const result = normalizeJobResult(rawResult);
        return (
          <div className="jq-modal-backdrop" onClick={() => { setSelectedJob(null); setJobSuppliers([]); }}>
            <div className="jq-modal" onClick={e => e.stopPropagation()}>
              <div className="jq-modal-header">
                <div>
                  <h3>Scrape Results</h3>
                  <div className="jq-modal-meta">
                    <span><Building2 size={12} /> {params.departmentCode}</span>
                    <span>{params.acquisitionType}</span>
                    <span><Calendar size={12} /> {params.dateFrom} → {params.dateTo}</span>
                    <span><Database size={12} /> {result?.totalPOs || 0} POs
                      {result?.duplicateRate >= 95 ? ' (all dup)' : result?.duplicateCount > 0 ? ` (${result.duplicateCount} dup)` : ''}
                    </span>
                    <span><Clock size={12} /> {formatDuration(result?.duration)}</span>
                  </div>
                </div>
                <div className="jq-modal-actions">
                  <button className="jq-copy-btn" onClick={handleCopyAll}>
                    <Copy size={12} /> {copied ? 'Copied!' : 'Copy All'}
                  </button>
                  <button className="jq-close-btn" onClick={() => { setSelectedJob(null); setJobSuppliers([]); }}>
                    <X size={18} />
                  </button>
                </div>
              </div>

              <div className="jq-modal-body">
                {/* Job Summary Section - Always show this */}
                <div className="jq-job-summary">
                  <div className="jq-summary-grid">
                    <div className="jq-summary-stat">
                      <span className="jq-summary-label">Total POs</span>
                      <span className="jq-summary-value">{result?.totalPOs || 0}</span>
                    </div>
                    <div className="jq-summary-stat">
                      <span className="jq-summary-label">New/Updated</span>
                      <span className="jq-summary-value jq-summary-new">{result?.newCount || 0}</span>
                    </div>
                    <div className="jq-summary-stat">
                      <span className="jq-summary-label">Skipped (dup)</span>
                      <span className="jq-summary-value jq-summary-dup">{result?.duplicateCount || 0}</span>
                    </div>
                    <div className="jq-summary-stat">
                      <span className="jq-summary-label">Duration</span>
                      <span className="jq-summary-value">{formatDuration(result?.duration)}</span>
                    </div>
                    {result?.totalLineItems > 0 && (
                      <div className="jq-summary-stat">
                        <span className="jq-summary-label">Line Items</span>
                        <span className="jq-summary-value">{result.totalLineItems.toLocaleString()}</span>
                      </div>
                    )}
                    {result?.linesUpserted > 0 && (
                      <div className="jq-summary-stat">
                        <span className="jq-summary-label">Lines Saved</span>
                        <span className="jq-summary-value">{result.linesUpserted.toLocaleString()}</span>
                      </div>
                    )}
                    {(result?.classificationSummary?.classifiedPct != null || result?.classificationRate != null) && (
                      <div className="jq-summary-stat">
                        <span className="jq-summary-label">Classified</span>
                        <span className="jq-summary-value">{Math.round(result.classificationSummary?.classifiedPct ?? result.classificationRate)}%</span>
                      </div>
                    )}
                    {(result?.classificationSummary?.avgConfidence != null || result?.avgConfidence != null) && (
                      <div className="jq-summary-stat">
                        <span className="jq-summary-label">Avg Confidence</span>
                        <span className="jq-summary-value">{Math.round((result.classificationSummary?.avgConfidence ?? result.avgConfidence) * 100)}%</span>
                      </div>
                    )}
                    {((result?.classificationSummary?.needsReview ?? result?.needsReview) > 0) && (
                      <div className="jq-summary-stat">
                        <span className="jq-summary-label">Needs Review</span>
                        <span className="jq-summary-value jq-summary-review">{result.classificationSummary?.needsReview ?? result.needsReview}</span>
                      </div>
                    )}
                  </div>
                  {result?.runId && (
                    <div className="jq-summary-runid">
                      <span className="jq-summary-label">Run ID:</span>
                      <code>{result.runId}</code>
                    </div>
                  )}
                </div>

                {/* Suppliers List or Empty State */}
                {suppliersLoading ? (
                  <div className="jq-modal-loading"><Loader2 size={20} className="jq-spin" /> Loading PO records...</div>
                ) : jobSuppliers.length === 0 ? (
                  <div className="jq-modal-empty-section">
                    {result?.duplicateRate >= 95 || (result?.duplicateCount > 0 && (result?.newCount || 0) === 0) ? (
                      <div className="jq-empty-message">
                        <AlertCircle size={18} style={{ color: '#eab308' }} />
                        <span>All POs were duplicates - they already exist in the database from previous scrapes.</span>
                      </div>
                    ) : (result?.newCount || 0) === 0 && (result?.duplicateCount || 0) === 0 ? (
                      <div className="jq-empty-message">
                        <AlertCircle size={18} style={{ color: '#64748b' }} />
                        <span>No POs found in this date range for the selected department.</span>
                      </div>
                    ) : (
                      <div className="jq-empty-message">
                        <AlertCircle size={18} style={{ color: '#64748b' }} />
                        <span>PO details not available for drill-down. Data was processed but individual records cannot be fetched.</span>
                      </div>
                    )}
                  </div>
                ) : (
                  <table className="jq-supplier-table">
                    <thead>
                      <tr>
                        <th>Supplier</th>
                        <th>Amount</th>
                        <th>Document #</th>
                        <th>Department</th>
                        <th>Date</th>
                        <th>Description</th>
                      </tr>
                    </thead>
                    <tbody>
                      {jobSuppliers.map((s, i) => (
                        <tr key={s.id || i}>
                          <td className="jq-td-supplier">
                            <span className="jq-supplier-name">{s.supplier_name || '-'}</span>
                            {s.supplier_number && <span className="jq-supplier-id">{s.supplier_number}</span>}
                          </td>
                          <td className="jq-td-amount">${parseFloat(s.amount || 0).toLocaleString()}</td>
                          <td className="jq-td-doc">
                            {s.document_number ? <PoLink documentNumber={s.document_number} /> : '-'}
                          </td>
                          <td className="jq-td-dept">{s.department_name || s.department_code || '-'}</td>
                          <td className="jq-td-date">{s.date || '-'}</td>
                          <td className="jq-td-desc" title={s.description}>{(s.description || '-').substring(0, 40)}{s.description?.length > 40 ? '…' : ''}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {/* Failed Job Detail Modal */}
      {selectedFailedJob && (() => {
        const params = typeof selectedFailedJob.params === 'string' ? JSON.parse(selectedFailedJob.params) : (selectedFailedJob.params || {});
        return (
          <div className="jq-modal-backdrop" onClick={() => setSelectedFailedJob(null)}>
            <div className="jq-modal jq-failed-modal" onClick={e => e.stopPropagation()}>
              <div className="jq-modal-header">
                <div>
                  <h3><AlertCircle size={18} style={{ color: '#ef4444' }} /> Failed Job</h3>
                  <div className="jq-modal-meta">
                    <span><Building2 size={12} /> {deptMap[params.departmentCode] || params.departmentCode}</span>
                    <span>{params.acquisitionType || 'All Types'}</span>
                    <span><Calendar size={12} /> {params.dateFrom} → {params.dateTo}</span>
                    {selectedFailedJob.completed_at && (
                      <span><Clock size={12} /> Failed {timeAgo(selectedFailedJob.completed_at)}</span>
                    )}
                  </div>
                </div>
                <button className="jq-close-btn" onClick={() => setSelectedFailedJob(null)}>
                  <X size={18} />
                </button>
              </div>

              <div className="jq-modal-body">
                <div className="jq-failed-error-section">
                  <h4>Error Details</h4>
                  <div className="jq-failed-error-box">
                    {selectedFailedJob.error || 'Unknown error occurred'}
                  </div>
                </div>

                <div className="jq-failed-info-section">
                  <h4>Job Information</h4>
                  <div className="jq-failed-info-grid">
                    <div className="jq-failed-info-item">
                      <span className="jq-failed-info-label">Job ID</span>
                      <span className="jq-failed-info-value">{selectedFailedJob.id}</span>
                    </div>
                    <div className="jq-failed-info-item">
                      <span className="jq-failed-info-label">Type</span>
                      <span className="jq-failed-info-value">{selectedFailedJob.type || 'scprs'}</span>
                    </div>
                    <div className="jq-failed-info-item">
                      <span className="jq-failed-info-label">Created</span>
                      <span className="jq-failed-info-value">{selectedFailedJob.created_at ? new Date(selectedFailedJob.created_at).toLocaleString() : '-'}</span>
                    </div>
                    <div className="jq-failed-info-item">
                      <span className="jq-failed-info-label">Started</span>
                      <span className="jq-failed-info-value">{selectedFailedJob.started_at ? new Date(selectedFailedJob.started_at).toLocaleString() : '-'}</span>
                    </div>
                    {selectedFailedJob.created_by && selectedFailedJob.created_by !== 'unknown' && (
                      <div className="jq-failed-info-item">
                        <span className="jq-failed-info-label">Created By</span>
                        <span className="jq-failed-info-value">{selectedFailedJob.created_by}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="jq-modal-footer">
                <button
                  className="jq-btn jq-btn-retry"
                  onClick={() => handleRetryFailedJob(selectedFailedJob.id)}
                  disabled={retrying}
                >
                  <RefreshCw size={14} className={retrying ? 'jq-spin' : ''} />
                  {retrying ? 'Retrying...' : 'Retry Job'}
                </button>
                <button
                  className="jq-btn jq-btn-delete"
                  onClick={() => handleDeleteFailedJob(selectedFailedJob.id)}
                >
                  <Trash2 size={14} />
                  Delete Job
                </button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}

export default JobQueue;
